import { useTranslation } from "react-i18next";
import { CARD, FIGURE, GROUP_EDGE, META_LABEL } from "./coverageStyles";

const ICON = {
  conducted:
    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  people:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  eye: "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
};

// A readout, never a control. These four used to be buttons — two jumped the
// table to a filter, one poked the queue open — and a row where some cards
// answer a click and others do not is a row you have to try to understand.
// Everything they linked to is on the same screen already: the filter select
// sits above the table, and the queue is directly below.
function Shell({ label, iconPath, iconBg, iconColor, children }) {
  return (
    <div className={`${CARD} p-6`}>
      <div className="flex items-start justify-between gap-3">
        <p className={META_LABEL}>{label}</p>
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconBg}`}>
          <svg
            className={`h-[18px] w-[18px] ${iconColor}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        </span>
      </div>
      {children}
    </div>
  );
}

// "N of M" is the only honest shape for coverage: a bare 9 hides whether the
// roster is ten people or eighty. The denominator is the people who can do the
// thing — not the whole roster — so the tile and the filter option below it
// count the same population.
function Ratio({ done, total, accent = "text-slate-900" }) {
  const { t } = useTranslation();
  if (done == null || total == null) return <p className={`mt-4 text-4xl ${FIGURE} text-slate-300`}>—</p>;
  return (
    <p className={`mt-4 text-4xl ${FIGURE} ${accent}`}>
      {done}
      <span className="ml-1.5 text-base font-bold text-slate-400">
        {t("coverage.tiles.ofTotal", { total })}
      </span>
    </p>
  );
}

// The sub-line's words come from the FILTER's own key, not from a second string
// that happens to match today: the tile is the answer and the filter option is
// the action on it, and the only way that stays true through three languages is
// for both to render the same key. The count is a span rather than an
// interpolation for exactly the same reason — a `{{count}}` in the sentence
// would force a second, tile-only key the filter could not reuse.
function Sub({ bullet, label, count }) {
  return (
    <p className="mt-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${bullet}`} />
      <span className="truncate">{label}</span>
      {count != null && <span className="shrink-0 tabular-nums text-slate-700">{count}</span>}
    </p>
  );
}

// One payload answers all four tiles. They used to need two — the roster was
// side-relative, so "who conducted" and "who was visited" came from two
// requests and a mirrored second call existed only to fill this row. One
// roster, both denominators, one request.
export default function CoverageTiles({ totals }) {
  const { t } = useTranslation();
  const overdue = totals?.overdue ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Official and peer are never added up. They are different acts — an
          evaluation and a colleague's visit — and one headline made of both
          reads as an evaluation count twice as large as it is. */}
      <Shell
        label={t("coverage.tiles.conducted")}
        iconPath={ICON.conducted}
        iconBg="bg-indigo-50"
        iconColor="text-indigo-600"
      >
        <div className="mt-4 flex items-stretch gap-4">
          <div className="min-w-0 flex-1">
            <p className={`text-4xl ${FIGURE} text-slate-900`}>{totals?.official ?? 0}</p>
            <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t("coverage.totals.official")}
            </p>
          </div>
          {/* The same hairline the table draws between its two role groups. */}
          <div className={`shrink-0 ${GROUP_EDGE}`} aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className={`text-4xl ${FIGURE} text-slate-400`}>{totals?.peer ?? 0}</p>
            <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t("coverage.totals.peer")}
            </p>
          </div>
        </div>
      </Shell>

      <Shell
        label={t("coverage.tiles.whoConducted")}
        iconPath={ICON.people}
        iconBg="bg-violet-50"
        iconColor="text-violet-600"
      >
        <Ratio
          done={totals ? totals.can_conduct - totals.none_conducted : null}
          total={totals ? totals.can_conduct : null}
        />
        <Sub
          bullet={totals?.none_conducted ? "bg-rose-500" : "bg-emerald-500"}
          label={totals ? t("coverage.filter.noneConducted") : t("dashboard.panelError")}
          count={totals ? totals.none_conducted : null}
        />
      </Shell>

      <Shell
        label={t("coverage.totals.overdue")}
        iconPath={ICON.clock}
        iconBg={overdue > 0 ? "bg-rose-50" : "bg-emerald-50"}
        iconColor={overdue > 0 ? "text-rose-600" : "text-emerald-600"}
      >
        <p className={`mt-4 text-4xl ${FIGURE} ${overdue > 0 ? "text-rose-600" : "text-slate-900"}`}>
          {overdue}
        </p>
        {/* The three ways a booking fails to become a visit, together: nobody
            ran it, the teacher refused, the observer took it back. All three
            are dated by scheduled_date inside the selected period, so they are
            one story now — and they stay three numbers, because a visit still
            owed, one refused and one withdrawn need three different answers. */}
        <Sub
          bullet={overdue > 0 ? "bg-amber-500" : "bg-emerald-500"}
          label={t("coverage.tiles.closedSplit", {
            declined: totals?.declined ?? 0,
            withdrawn: totals?.withdrawn ?? 0,
          })}
        />
      </Shell>

      <Shell
        label={t("coverage.tiles.teacherCoverage")}
        iconPath={ICON.eye}
        iconBg="bg-emerald-50"
        iconColor="text-emerald-600"
      >
        <Ratio
          done={totals ? totals.can_be_observed - totals.uncovered : null}
          total={totals ? totals.can_be_observed : null}
        />
        <Sub
          bullet={totals?.uncovered ? "bg-rose-500" : "bg-emerald-500"}
          label={totals ? t("coverage.filter.uncovered") : t("dashboard.panelError")}
          count={totals ? totals.uncovered : null}
        />
      </Shell>
    </div>
  );
}
